import { forwardRef, MouseEventHandler, ReactNode } from 'react';
import Button, { ButtonProps } from '../Button';
import { styled } from '@mui/material/styles';
import { handleBreakpoints } from '@mui/system';

interface BreakpointedMaxWidth {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

export interface AppBarButtonProps extends Omit<ButtonProps, 'variant'> {
  children?: ReactNode;
  variant?: 'dense' | string; // ToDo: finish variant specs, disclude string from it
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: MouseEventHandler;
  maxWidth?: BreakpointedMaxWidth;
}

interface AppBarButtonRootProps {
  ownerState: {
    dense?: boolean;
  };
}

interface AppBarButtonOwnerState {
  maxWidth?: BreakpointedMaxWidth;
  shouldClipText?: boolean;
  dense: boolean;
}

interface AppBarClippedProps {
  ownerState: AppBarButtonOwnerState;
}

const AppBarButtonRoot = styled(Button, {
  name: 'O2AppBar',
  slot: 'Button',
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<AppBarButtonRootProps>(({ theme, ownerState }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  borderRadius: 0,
  alignSelf: 'stretch',
  padding: `0 ${theme.spacing(1.25)}`,
  fontWeight: theme.typography.fontWeightRegular,
  ...(!ownerState.dense && {
    [theme.breakpoints.up('sm')]: {
      padding: `0 ${theme.spacing(2)}`,
    },
  }),
  color: theme.palette.common.white,
  '&.MuiButton-textInternal': {
    color: theme.palette.internal.contrastText,
  },
}));

const AppBarClipped = styled('div', {
  name: 'O2AppBar',
  slot: 'Wrapper',
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<AppBarClippedProps>(({ theme, ownerState }) => ({
  ...(ownerState.shouldClipText && {
    textOverflow: 'ellipsis',
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    verticalAlign: 'middle',
    ...handleBreakpoints({ theme }, ownerState.maxWidth, (propValue) => ({
      maxWidth: propValue || 'none',
    })),
  }),
}));

const AppBarButton = forwardRef<HTMLButtonElement, AppBarButtonProps>((props, ref) => {
  const { variant, maxWidth, children, ...other } = props;

  const ownerState: AppBarButtonOwnerState = {
    dense: variant === 'dense',
    shouldClipText: maxWidth && Object.values(maxWidth).reduce((i, r) => i + r, 0) > 0,
    maxWidth,
  };

  return (
    <AppBarButtonRoot ref={ref} ownerState={ownerState} color="internal" variant="text" {...other}>
      {ownerState.shouldClipText ? <AppBarClipped ownerState={ownerState}>{children}</AppBarClipped> : children}
    </AppBarButtonRoot>
  );
});

AppBarButton.displayName = 'AppBar.Button';

export default AppBarButton;
